import { useState, useCallback } from 'react';
import { useModelStore } from '../store/useModelStore';
import { 
  validateSelectionForCopyPaste, 
  getCompletelySelectedElements,
  SelectionBounds 
} from '../utilities/SelectionValidationUtils';
import * as THREE from 'three';

interface UseCopyPasteReturn {
  // Dialog state
  isOffsetDialogOpen: boolean;
  selectedCount: {
    nodes: number;
    beams: number;
  };
  
  // Dialog handlers
  showOffsetDialog: () => void;
  hideOffsetDialog: () => void;
  
  // Copy-paste operations
  copySelected: (validationParams?: {
    selectionBounds: SelectionBounds;
    camera: THREE.Camera;
    canvasRect: DOMRect;
  }) => void;
  pasteWithOffset: (params: { x: number; y: number; z: number; copies: number }) => void;
  
  // Selection helpers
  hasSelection: boolean;
  hasCopiedElements: boolean;
  canCopyPaste: boolean;
}

export const useCopyPaste = (): UseCopyPasteReturn => {
  const [isOffsetDialogOpen, setIsOffsetDialogOpen] = useState(false);
  const [canCopyPaste, setCanCopyPaste] = useState(false);
  
  const {
    selectedNodeIds,
    selectedBeamIds,
    nodes,
    beams,
    copiedNodes,
    copiedBeams,
    copySelected: storeCopySelected,
    pasteWithOffset: storePasteWithOffset,
    restoreDefaultColors
  } = useModelStore();

  const selectedCount = {
    nodes: selectedNodeIds.length,
    beams: selectedBeamIds.length
  };

  const hasSelection = selectedNodeIds.length > 0 || selectedBeamIds.length > 0;
  const hasCopiedElements = copiedNodes.length > 0 || copiedBeams.length > 0;

  const showOffsetDialog = useCallback(() => {
    setIsOffsetDialogOpen(true);
  }, []);

  const hideOffsetDialog = useCallback(() => {
    setIsOffsetDialogOpen(false);
  }, []);

  const copySelected = useCallback((validationParams?: {
    selectionBounds: SelectionBounds;
    camera: THREE.Camera;
    canvasRect: DOMRect;
  }) => {
    if (!hasSelection) {
      //console.log('📋 No elements selected to copy');
      return;
    }

    // If validation parameters are provided, check for complete containment
    if (validationParams) {
      const validation = validateSelectionForCopyPaste(
        selectedNodeIds,
        selectedBeamIds,
        nodes,
        beams,
        validationParams.selectionBounds,
        validationParams.camera,
        validationParams.canvasRect
      );

      if (!validation.isValid) {
        //console.log('📋 Copy cancelled:', validation.reason);
        setCanCopyPaste(false);
        return;
      }

      // Get completely selected elements for copy
      const completelySelected = getCompletelySelectedElements(
        selectedNodeIds,
        selectedBeamIds,
        nodes,
        beams,
        validationParams.selectionBounds,
        validationParams.camera,
        validationParams.canvasRect
      );

      // Only copy if at least one element is completely selected
      if (completelySelected.nodes.length === 0 && completelySelected.beams.length === 0) {
        //console.log('📋 No elements are completely selected. Copy cancelled.');
        setCanCopyPaste(false);
        return;
      }

      //console.log('📋 Copying completely selected elements:', completelySelected);
      setCanCopyPaste(true);
    } else {
      // No validation - use existing behavior
      setCanCopyPaste(true);
    }
    
    storeCopySelected();
    //console.log('📋 Elements copied to clipboard');
  }, [hasSelection, storeCopySelected, selectedNodeIds, selectedBeamIds, nodes, beams]);

  const pasteWithOffset = useCallback((params: { x: number; y: number; z: number; copies: number }) => {
    if (!hasCopiedElements) {
      //console.log('📋 No elements in clipboard to paste');
      return;
    }

    // Create multiple copies with cumulative offsets
    for (let i = 0; i < params.copies; i++) {
      const offset = {
        x: params.x * (i + 1),
        y: params.y * (i + 1),
        z: params.z * (i + 1),
        copies: 1 // Each iteration creates 1 copy
      };
      storePasteWithOffset(offset);
    }
    
    setIsOffsetDialogOpen(false);
    restoreDefaultColors();
    
    //console.log('📋 Elements pasted', params.copies, 'times with offset:', params);
  }, [hasCopiedElements, storePasteWithOffset, restoreDefaultColors]);

  return {
    isOffsetDialogOpen,
    selectedCount,
    showOffsetDialog,
    hideOffsetDialog,
    copySelected,
    pasteWithOffset,
    hasSelection,
    hasCopiedElements,
    canCopyPaste
  };
};

export default useCopyPaste;